import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Typography,
  Space,
  Modal,
  Form,
  Select,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Card } from "antd";
import "./index.css";
import { ROUTES } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import {
  getHdrDetails,
  GetOHDRAVF,
  GetOHDRBloodTubingCount,
  GetOHDRDialysisPerformedBy,
  GetOHDRDialyzerCount,
  GetOHDRFullBloodVolume,
  GetOHDRHeparin,
  GetOHDRNS,
  GetOHDRVascularAccessTypes,
  postHdrDetails,
  updateHdrDetails,
} from "../../apis/nephroPlusService/OHDRService";
import { errorMessage, successMessage } from "../../utils/notificationUtils";
import { useMediaQuery } from "react-responsive";

const { Text, Title } = Typography;
const { Option } = Select;

// Define the type for each row of HDR data
export type HDRData = {
  id: string;
  patientName: string;
  patientId?: string;
  access?: string;
  dialyzer?: string;
  bt?: string;
  fbv?: string;
  heparin?: string;
  g16?: string;
  g17?: string;
  ns1000?: string;
  ns500?: string;
  injection?: string;
  remark?: string;
  date?: string;
  dialysisDoneBy?: string;
  billNo?: string; // <-- Added missing property
};

export type HDRListProps = {
  data: HDRData[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onFillHDR: (id: string) => void;
  onSearch: (search: string) => void;
  pagination: any;
  handleTableChange: any;
  fetchHDRList?: any;
};

const HDRDetailModal = ({
  open,
  onClose,
  record,
  fetchHDRList,
  pagination,
}: {
  open: boolean;
  onClose: () => void;
  record: any;
  fetchHDRList: any;
  pagination: any;
}) => {
  const [form] = Form.useForm();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1024 });

  const fetchHdrDetails = async () => {
    try {
      const res = await getHdrDetails(record.visitId);
      form.setFieldsValue({
        patientName: record.patientName,
        ...res?.data,
      });
    } catch (err: any) {
      // errorMessage("Please, Try again later");
    }
  };

  useEffect(() => {
    fetchHdrDetails();
    if (record) {
      form.setFieldsValue({
        patientName: record.patientName,
      });
    } else {
      form.resetFields();
    }
    // form.resetFields();
  }, [record, form]);

  const [dialysisPerformedBy, setDialysisPerformedBy] = useState([]);
  const [ns, setNS] = useState([]);
  const [avf, setAVF] = useState([]);
  const [heparin, setHeparin] = useState([]);
  const [fullBloodVolume, setFullBloodVolume] = useState([]);
  const [bloodTubingCount, setBloodTubingCount] = useState([]);
  const [dialyzerCount, setDialyzerCount] = useState([]);
  const [vascularAccessTypes, setVascularAccessTypes] = useState([]);

  const fetchDialysisPerformedBy = async () => {
    try {
      const response = await GetOHDRDialysisPerformedBy();
      setDialysisPerformedBy(response.data || []);
    } catch (e) {
      console.error("Error fetching dialysis performed by data:", e);
    }
  };

  const fetchNS = async () => {
    try {
      const response = await GetOHDRNS();
      setNS(response.data || []);
    } catch (e) {
      console.error("Error fetching NS data:", e);
    }
  };

  const fetchAVF = async () => {
    try {
      const response = await GetOHDRAVF();
      setAVF(response.data || []);
    } catch (e) {
      console.error("Error fetching AVF data:", e);
    }
  };

  const fetchHeparin = async () => {
    try {
      const response = await GetOHDRHeparin();
      setHeparin(response.data || []);
    } catch (e) {
      console.error("Error fetching Heparin data:", e);
    }
  };

  const fetchFullBloodVolume = async () => {
    try {
      const response = await GetOHDRFullBloodVolume();
      setFullBloodVolume(response.data || []);
    } catch (e) {
      console.error("Error fetching Full Blood Volume data:", e);
    }
  };

  const fetchBloodTubingCount = async () => {
    try {
      const response = await GetOHDRBloodTubingCount();
      setBloodTubingCount(response.data || []);
    } catch (e) {
      console.error("Error fetching Blood Tubing Count data:", e);
    }
  };

  const fetchDialyzerCount = async () => {
    try {
      const response = await GetOHDRDialyzerCount();
      setDialyzerCount(response.data || []);
    } catch (e) {
      console.error("Error fetching Dialyzer Count data:", e);
    }
  };

  const fetchVascularAccessTypes = async () => {
    try {
      const response = await GetOHDRVascularAccessTypes();
      setVascularAccessTypes(response.data || []);
    } catch (e) {
      console.error("Error fetching Vascular Access Types data:", e);
    }
  };

  useEffect(() => {
    fetchDialysisPerformedBy();
    fetchNS();
    fetchAVF();
    fetchHeparin();
    fetchFullBloodVolume();
    fetchBloodTubingCount();
    fetchDialyzerCount();
    fetchVascularAccessTypes();
  }, []);

  const handleSave = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);
      const formData = {
        ...values,
        caseId: record?.caseId,
        visitId: record?.visitId,
      };

      const res = await postHdrDetails(formData);
      if (res) {
      }
      successMessage("Data Saved Successfully");
      fetchHDRList(pagination.current, pagination.pageSize);
      onClose();
    } catch (err: any) {
      if (err?.errorFields?.length > 0) {
        errorMessage("Please, fill all details before submitting");
      } else {
        errorMessage("Please, Try again later");
      }
    }
  };

  const handleUpdate = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);
      const formData = {
        ...values,
        caseId: record?.caseId,
        visitId: record?.visitId,
      };

      const res = await updateHdrDetails(formData);
      if (res) {
        successMessage("Data Updated Successfully");
        fetchHDRList(pagination.current, pagination.pageSize);
        onClose();
      }
    } catch (err: any) {
      if (err?.errorFields?.length > 0) {
        errorMessage("Please, fill all details before submitting");
      } else {
        errorMessage("Please, Try again later");
      }
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<span style={{ fontWeight: "bold" }}>HDR DETAIL</span>}
      footer={null}
      centered
      width={isMobile ? "98vw" : isTablet ? "90vw" : 700}
      // width={700}
      destroyOnClose
      className="hdr-detail-modal"
      height={`${isMobile ? "auto" : "80%"}`}
      bodyStyle={{
        padding: isMobile ? 8 : 24,
        maxHeight: isMobile ? "120vh" : "auto",
        overflowY: "auto",
      }}
    >
      <Form
        layout="vertical"
        form={form}
        className="hdr-detail-modal"
        style={{
          width: "100%",
        }}
      >
        <Form.Item label="PATIENT NAME" name="patientName">
          <Input disabled />
        </Form.Item>
        <div style={{ display: isMobile ? "block" : "flex", gap: 8 }}>
          <Form.Item
            label="ACCESS"
            name="vascularAccessType"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Please select access type" }]}
          >
            <Select
              options={vascularAccessTypes?.map((item: any) => ({
                label: item.vascularAccessType,
                value: item.vascularAccessType,
              }))}
              showSearch
              placeholder="Select"
            />
          </Form.Item>
          <Form.Item
            label="DIALYZER"
            name="dialyzerReuseCount"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Please select dialyzer" }]}
          >
            <Select
              options={dialyzerCount?.map((item: any) => ({
                label: item.dialyzerReuseCount,
                value: item.dialyzerReuseCount,
              }))}
              showSearch
              placeholder="Select"
            />
          </Form.Item>
          <Form.Item
            label="BT"
            name="bloodTubingCount"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Please select BT" }]}
          >
            <Select
              options={bloodTubingCount?.map((item: any) => ({
                label: item.bloodTubingCount,
                value: item.bloodTubingCount,
              }))}
              showSearch
              placeholder="Select"
            />
          </Form.Item>
        </div>
        <div style={{ display: isMobile ? "block" : "flex", gap: 8 }}>
          <Form.Item
            label="FBV"
            name="fullBloodVolume"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Please select FBV" }]}
          >
            <Select
              options={fullBloodVolume?.map((item: any) => ({
                label: item.fullBloodVolume,
                value: item.fullBloodVolume,
              }))}
              showSearch
              placeholder="Select"
            />
          </Form.Item>
          <Form.Item
            label="HEPARIN"
            name="heparinVolumeMl"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Please select heparin" }]}
          >
            <Select
              options={heparin?.map((item: any) => ({
                label: item.heparinVolumeMl,
                value: item.heparinVolumeMl,
              }))}
              showSearch
              placeholder="Select"
            />
          </Form.Item>
          <Form.Item
            label="AVF 16G"
            name="avf16G"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Please select AVF (16G)" }]}
          >
            <Select
              options={avf?.map((item: any) => ({
                label: item.avf,
                value: item.avf,
              }))}
              placeholder="Select"
            />
          </Form.Item>
        </div>
        <div style={{ display: isMobile ? "block" : "flex", gap: 8 }}>
          <Form.Item
            label="AVF 17G"
            name="avf17G"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Please select AVF (17G)" }]}
          >
            <Select
              options={avf?.map((item: any) => ({
                label: item.avf,
                value: item.avf,
              }))}
              placeholder="Select"
            />
          </Form.Item>
          <Form.Item
            label="NS 1000 ML"
            name="ns1000ml"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Please select NS (1000 ml)" }]}
          >
            <Select
              style={{ width: "100%" }}
              options={ns?.map((item: any) => ({
                label: item.ns,
                value: item.ns,
              }))}
              showSearch
              placeholder="Select NS (1000 ml)"
            />
          </Form.Item>
          <Form.Item
            label="NS (500 ml)"
            name="ns500ml"
            style={{ flex: 1 }}
            rules={[{ required: true, message: "Please select NS (500 ml)" }]}
          >
            <Select
              style={{ width: "100%" }}
              options={ns?.map((item: any) => ({
                label: item.ns,
                value: item.ns,
              }))}
              showSearch
              placeholder="Select NS (500 ml)"
            />
          </Form.Item>
        </div>
        <div style={{ display: isMobile ? "block" : "flex", gap: 8 }}>
          <Form.Item
            label="INJECTIONS"
            name="hasReceivedInjection"
            style={{ flex: 1 }}
            rules={[
              { required: true, message: "Please select injection option" },
            ]}
          >
            <Select
              options={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
              placeholder="Select"
            />
          </Form.Item>
          <Form.Item
            label="Dialysis Done by"
            name="dialysisDoneBy"
            style={{ flex: 1 }}
            rules={[
              {
                required: true,
                message: "Please select who performed dialysis",
              },
            ]}
          >
            <Select
              style={{ width: "100%" }}
              options={dialysisPerformedBy?.map((item: any) => ({
                label: item.dialysisDoneBy,
                value: item.dialysisDoneBy,
              }))}
              showSearch
              placeholder="Select who performed dialysis"
            />
          </Form.Item>
          <Form.Item
            label="Bill No."
            name="billNo"
            style={{ flex: 1 }}
            // rules={[
            //   {
            //     required: true,
            //     message: "Please enter bill no.",
            //   },
            // ]}
          >
            <Input />
          </Form.Item>
        </div>
        <Form.Item label="Remarks" name="remark">
          <Input.TextArea rows={2} />
        </Form.Item>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 16,
            marginTop: 16,
          }}
        >
          <Button
            onClick={onClose}
            style={{
              color: "#004C8F",
              boxShadow: "0px 2px 8px 0px #00000040",
              width: isMobile ? "40%" : "16%",
              borderRadius: "8px",
              border: "1px solid #004C8F",
            }}
          >
            CANCEL
          </Button>
          {!record?.isHdrFilled ? (
            <Button
              type="primary"
              style={{
                backgroundColor: "#004C8F",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: isMobile ? "40%" : "16%",
                borderRadius: "8px",
              }}
              onClick={handleSave}
            >
              Save
            </Button>
          ) : (
            <Button
              type="primary"
              style={{
                backgroundColor: "rgb(0, 76, 143)",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: isMobile ? "40%" : "16%",
                borderRadius: "8px",
              }}
              onClick={handleUpdate}
            >
              Update
            </Button>
          )}
        </div>
      </Form>
    </Modal>
  );
};

const HDRList: React.FC<HDRListProps> = ({
  data,
  total,
  page,
  pageSize,
  onPageChange,
  onFillHDR,
  onSearch,
  pagination,
  handleTableChange,
  fetchHDRList,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1024 });

  const columns = (onFillHDR: (id: any) => void) => [
    {
      title: "Patient Name",
      dataIndex: "patientName",
      key: "patientName",
      render: (text: string, record: any) => (
        <Text
          style={{
            color: "#1677ff",
            cursor: "pointer",
            textDecoration: "underline",
            fontWeight: "bold",
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigate({ pathname: `${ROUTES.OHDR_DETAILS}/${record.id}` });
          }}
        >
          {text}
        </Text>
      ),
      // fixed: !isMobile ? "left" : undefined,
      width: 140,
    },
    {
      title: "Appointment Date",
      dataIndex: "appointmentDate",
      key: "appointmentDate",
      width: 110,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "Visit Count",
      dataIndex: "visitCount",
      key: "visitCount",
      width: 90,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "Access",
      dataIndex: "vascularAccessType",
      key: "vascularAccessType",
      width: 80,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "Dialyzer",
      dataIndex: "dialyzerReuseCount",
      key: "dialyzerReuseCount",
      width: 100,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "BT",
      dataIndex: "bloodTubingCount",
      key: "bloodTubingCount",
      width: 60,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "FBV",
      dataIndex: "fullBloodVolume",
      key: "fullBloodVolume",
      width: 70,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "Heparin",
      dataIndex: "heparinVolumeMl",
      key: "heparinVolumeMl",
      width: 80,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "avf16G",
      dataIndex: "avf16G",
      key: "avf16G",
      width: 60,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "avf17G",
      dataIndex: "avf17G",
      key: "avf17G",
      width: 60,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "ns1000ml",
      dataIndex: "ns1000ml",
      key: "ns1000ml",
      width: 70,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "ns500ml",
      dataIndex: "ns500ml",
      key: "ns500ml",
      width: 70,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "Injection",
      dataIndex: "hasReceivedInjection",
      key: "hasReceivedInjection",
      width: 80,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      width: 90,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "Checked In Date/Time",
      dataIndex: "checkInDateTime",
      key: "checkInDateTime",
      width: 160,
      align: "center" as const,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MM-YYYY HH:mm") : "-",
    },
    {
      title: "Dialysis done by",
      dataIndex: "dialysisDoneBy",
      key: "dialysisDoneBy",
      width: 140,
      align: "center" as const,
      render: (text: string) => text?.toString() || "-",
    },
    {
      title: "Action",
      key: "action",
      // fixed: !isMobile ? "right" : undefined,
      width: 110,
      align: "center" as const,
      render: (_: any, record: any) => (
        <Space>
          {!record?.isHdrFilled ? (
            <Button
              type="primary"
              style={{
                backgroundColor: " #004C8F",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: "100%",
                borderRadius: "12px",
              }}
              onClick={() => onFillHDR(record.id)}
            >
              Fill HDR
            </Button>
          ) : (
            <Button
              icon={<EditOutlined />}
              onClick={() => onFillHDR(record.id)}
            />
          )}
        </Space>
      ),
    },
  ];

  const handleFillHDRClick = (id: string) => {
    const record = data?.find((row) => row.id === id) || null;
    setSelectedRecord(record);
    setModalOpen(true);
  };

  const tableScroll = isMobile
    ? { x: 900 }
    : isTablet
    ? { x: 1400 }
    : undefined;

  return (
    <div style={{ background: "#fff", borderRadius: 8, padding: 0 }}>
      <div
        style={{
          border: "0.4px solid #808184",
          padding: isMobile ? 8 : 20,
          borderBottomLeftRadius: "20px",
          borderBottomRightRadius: "20px",
        }}
      >
        <Table
          columns={columns(handleFillHDRClick)}
          dataSource={data}
          rowKey="id"
          bordered
          className="spaced-hdr-table"
          // scroll={tableScroll}
          // scroll={{ x: 1800 }}
          scroll={{
            x: "100%", 
            y: 500,
          }}
          pagination={{
            ...pagination,
            size: isMobile ? "small" : "middle",
            showSizeChanger: !isMobile,
          }}
          size={isMobile ? "small" : "middle"}
          style={{ background: "#fff" }}
          onChange={handleTableChange}
          rowClassName={(_, index) => (index % 2 === 0 ? "ohdr-row-alt" : "")}
        />
      </div>
      <HDRDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        record={selectedRecord}
        fetchHDRList={fetchHDRList}
        pagination={pagination}
      />
    </div>
  );
};

export default HDRList;
